import { Injectable } from '@angular/core';
import { UrlService } from '@service/url/url.service';
import { RestService } from 'cargo-chief-commons';
import { UtilService } from '@service/util/util.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { Equipment } from './equipment';
import { Subject } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Injectable({
	providedIn: 'root',
})
export class CarrierEquipmentDataService {
	dbList: Equipment[];
	equipmentList: Equipment[];
	laneCount: number = null;
	equipmentListUpdating = false;
	private _data = new Subject<Equipment[]>();
	observable = this._data.asObservable();
	private _laneCount = new Subject<number>();
	laneCountObservable = this._laneCount.asObservable();

	constructor(
		private urlService: UrlService,
		private restService: RestService,
		private utilService: UtilService
	) {}

	push(data: Equipment[]) {
		this._data.next(this.equipmentList);
	}

	pushLaneCount(count: number) {
		this.laneCount = count;
		this._laneCount.next(this.laneCount);
	}

	fetchEquipmentForCarrier(carrierId: string) {
		this.equipmentListUpdating = true;
		const url = this.urlService.equipmentForCarrier(carrierId);
		this.restService
			.GET(url)
			.pipe(
				finalize(() => {
					this.push(this.equipmentList);
					this.equipmentListUpdating = false;
				})
			)
			.subscribe(
				(result) => {
					this.equipmentList = result['content'];
				},
				(error) => {
					this.utilService.showSnackbar(
						'Unable to fetch equipment for carrier.',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
					this.equipmentList = [];
				}
			);
	}

	fetchLaneCountForCarrier(carrierId: string) {
		const url = this.urlService.carriersLanesCount();
		const payload = {
			CARRIER_IDS: [carrierId],
		};
		this.restService
			.POST(url, payload)
			.pipe(
				finalize(() => {
					this.pushLaneCount(this.laneCount);
				})
			)
			.subscribe(
				(result) => {
					if (!this.utilService.isNullOrUndefined(result.data) && result.data.length === 1) {
						if (!this.utilService.isNullOrUndefined(result.data[0].count)) {
							this.laneCount = result.data[0].count;
						}
					}
				},
				(error) => {
					this.utilService.showSnackbar(
						'Unable to fetch lane count for carrier.',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
					this.laneCount = null;
				}
			);
	}

	fetchAllEquipmentFromDb() {
		const url = this.urlService.equipmentInDB();
		this.restService.GET(url).subscribe(
			(result) => {
				this.dbList = result['content'];
			},
			(error) => {
				this.utilService.showSnackbar(
					'Unable to fetch equipment from DB.',
					SNACKBAR.ERROR,
					SNACKBAR.ACTION
				);
				this.dbList = [];
			}
		);
	}

	deleteEquipmentForCarrier(carrierId: string, equipmentId: string) {
		const promise = new Promise((resolve, reject) => {
			const url = this.urlService.removeEquipmentForCarrier(carrierId, equipmentId);
			this.restService
				.DELETE(url)
				.toPromise()
				.then(
					(success) => {
						resolve(success);
					},
					(error) => {
						reject(error);
					}
				);
		});
		return promise;
	}

	addEquipmentForCarrier(carrierId: string, equipmentId: string) {
		const promise = new Promise((resolve, reject) => {
			const url = this.urlService.addEquipmentForCarrier(carrierId, equipmentId);
			this.restService
				.POST(url, {})
				.toPromise()
				.then(
					(success) => {
						resolve(success);
					},
					(error) => {
						reject(error);
					}
				);
		});
		return promise;
	}

	mapTrailerTypeToEquipment(trailerType: string): Equipment {
		const equipment = this.dbList.filter((e) => {
			// special case for power only -->
			let trailer = trailerType.toLowerCase();
			if (trailer === 'power only') {
				trailer = 'power';
			}
			// <--
			return e.name.toLowerCase() === trailer;
		});
		if (!this.utilService.isNullOrUndefined(equipment) && equipment.length === 1) {
			return equipment[0];
		} else {
			return null;
		}
	}

	isInEquipmentList(equipment: Equipment) {
		return (
			this.equipmentList.filter((e) => {
				return e.id === equipment.id;
			}).length > 0
		);
	}
}
